"use client";

import { useSyncExternalStore } from "react";
import { EPISODE_PARAM } from "./ottEpisodes";

/**
 * The `?e=<id>` of the current URL as a store (N01, decision D32).
 *
 * The series page is cached per PATH, so the server render must not depend on
 * the query: this reads `null` on the server and during hydration, then the
 * real value on the very next client render — React's sanctioned way to let a
 * client-only fact correct the first paint without a hydration mismatch. A
 * viewer's pick is written back with `setEpisodeParam` (no navigation, no
 * history entry), and back/forward re-read it through `popstate`.
 */
const listeners = new Set<() => void>();

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  window.addEventListener("popstate", onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener("popstate", onChange);
  };
}

function read(): string | null {
  return new URLSearchParams(window.location.search).get(EPISODE_PARAM);
}

export function useEpisodeParam(): string | null {
  return useSyncExternalStore(subscribe, read, () => null);
}

/** Name the episode in the URL and tell every reader — a reload or a shared
 *  link then opens on it. */
export function setEpisodeParam(id: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set(EPISODE_PARAM, id);
  window.history.replaceState(window.history.state, "", url);
  listeners.forEach((cb) => cb());
}
